import type { IMoodRecordDraft } from '@shared/types';
import { deleteDraftImage } from '../../features/nativeBridge/lib/imageBridge';
import {
  nativeGetItem,
  nativeRemoveItem,
  nativeSetItem,
} from '../../features/nativeBridge/lib/storageBridge';

export const buildDraftId = (ownerId: string, moodDate: string): string =>
  `mood_draft_${ownerId}_${moodDate}`;

const indexKey = (ownerId: string): string => `draftsIndex_${ownerId}`;

const readIndex = async (ownerId: string): Promise<string[]> => {
  const raw = await nativeGetItem(indexKey(ownerId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeIndex = async (ownerId: string, ids: string[]): Promise<void> => {
  await nativeSetItem(indexKey(ownerId), JSON.stringify(ids));
};

/**
 * Save a draft to native storage.
 * @param draft - The draft to save.
 * @returns True if successful, false otherwise.
 */
export const saveDraft = async (draft: IMoodRecordDraft): Promise<boolean> => {
  const serialized = JSON.stringify(draft);
  const success = await nativeSetItem(draft.id, serialized);
  if (!success) return false;

  // Keep a "current" pointer up to date.
  setCurrentDraft(draft);

  // Always reconcile the index; includes() prevents duplicates.
  const ids = await readIndex(draft.ownerId);
  if (!ids.includes(draft.id)) {
    ids.push(draft.id);
    await writeIndex(draft.ownerId, ids);
  }
  return true;
};

/**
 * Load a draft from native storage.
 * @param ownerId - The owner ID.
 * @param moodDate - The mood date.
 * @returns The draft, or null if not found.
 */
export const loadDraft = async (
  ownerId: string,
  moodDate: string
): Promise<IMoodRecordDraft | null> => {
  try {
    const id = buildDraftId(ownerId, moodDate);
    const raw = await nativeGetItem(id);
    if (raw) {
      return JSON.parse(raw) as IMoodRecordDraft;
    }
  } catch {} /* no op; */
  return null;
};

/**
 * Delete a draft from native storage.
 * @param ownerId - The owner ID.
 * @param moodDate - The mood date.
 */
export const deleteDraft = async (ownerId: string, moodDate: string): Promise<void> => {
  const id = buildDraftId(ownerId, moodDate);
  await Promise.all([
    nativeRemoveItem(id),
    deleteDraftImage(ownerId, moodDate),
    removeCurrentDraft(),
    readIndex(ownerId).then((ids) => {
      const next = ids.filter((x) => x !== id);
      writeIndex(ownerId, next);
    }),
  ]);
};

export const listDraftIds = async (ownerId: string): Promise<string[]> => readIndex(ownerId);

const setCurrentDraft = (draft: IMoodRecordDraft): void => {
  try {
    localStorage.setItem('current_draft_key', JSON.stringify(draft));
  } catch (error) {
    console.error(`%cError setting current draft:`, 'background:#F40; color:#222;', {
      draft,
      error,
    });
  }
};

/**
 * Get the current draft from local storage.
 * @param ownerId - The owner ID.
 * @returns The current draft, or null if not found.
 */
export const getCurrentDraft = (): IMoodRecordDraft | null => {
  try {
    const raw = localStorage.getItem('current_draft_key');
    const currentDraft = raw ? (JSON.parse(raw) as IMoodRecordDraft) : null;
    if (currentDraft) {
      // console.log(
      //   `%cReturning this existing current draft:`,
      //   'background:#bada55; color:#222;',
      //   currentDraft
      // );
      return currentDraft;
    }
  } catch {} /* no op; */
  return null;
};

export const removeCurrentDraft = (): void => {
  localStorage.removeItem('current_draft_key');
};
